/**
 * DatabaseReadService.js
 * 
 * Service for reading database data from Firestore.
 */

// Firebase
import { collection, doc, getDoc, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

// Constants
const DATABASES_COLLECTION = collections.collections;
const sharedDatabaseSubscriptions = new Map();

const normalizeWorkspaceKey = (workspaceKey) => String(workspaceKey || '').trim();

function ensureSharedDatabaseSubscription(workspaceKey) {
  const key = normalizeWorkspaceKey(workspaceKey);
  if (!key) return null;

  const existing = sharedDatabaseSubscriptions.get(key);
  if (existing) return existing;

  const entry = {
    key,
    rows: [],
    listeners: new Set(),
    unsubscribe: null,
  };

  const q = query(
    collection(db, DATABASES_COLLECTION),
    where('workspaceKey', '==', key)
  );

  entry.unsubscribe = onSnapshot(
    q,
    (snapshot) => {
      entry.rows = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...(docSnap.data() || {}),
      }));

      entry.listeners.forEach((listener) => {
        try {
          listener(entry.rows);
        } catch (error) {
          console.error('[DatabaseReadService] shared listener error:', error);
        }
      });
    },
    (error) => {
      console.error('[DatabaseReadService] shared subscribe error:', error);
      entry.rows = [];
      entry.listeners.forEach((listener) => listener([]));
    }
  );

  sharedDatabaseSubscriptions.set(key, entry);
  return entry;
}

/**
 * Subscribes to databases for a specific workspace.
 *
 * @param {string} workspaceKey - The workspace key to scope databases.
 * @param {function} callback - Callback function to handle real-time updates.
 *
 * @returns {function(): void} Unsubscribe function
 */
export function subscribeToDatabases(workspaceKey, callback) {

  // Validate callback
  if (typeof callback !== 'function') {
    return () => { };
  }

  // Create query
  const q = workspaceKey
    ? query(collection(db, DATABASES_COLLECTION), where('workspaceKey', '==', workspaceKey))
    : query(collection(db, DATABASES_COLLECTION));

  // Subscribe to real-time updates
  return onSnapshot(
    q,
    (snapshot) => {
      const rows = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...(docSnap.data() || {}),
      }));
      callback(rows);
    },
    (error) => {
      console.error('[DatabaseReadService] subscribeToDatabases error:', error);
      callback([]);
    }
  );
}

/**
 * Shared workspace-scoped databases subscription.
 *
 * Reuses a single Firestore listener per workspace and fans out updates
 * to all UI subscribers (sidebar modules/buckets/databases).
 *
 * @param {string} workspaceKey - The workspace key to scope databases.
 * @param {function} callback - Callback function to handle updates.
 *
 * @returns {function(): void} Unsubscribe function
 */
export function subscribeToDatabasesShared(workspaceKey, callback) {

  // Validate callback
  if (typeof callback !== 'function') {
    return () => { };
  }

  const key = normalizeWorkspaceKey(workspaceKey);
  if (!key) {
    callback([]);
    return () => { };
  }

  const entry = ensureSharedDatabaseSubscription(key);
  if (!entry) {
    callback([]);
    return () => { };
  }

  entry.listeners.add(callback);
  callback(entry.rows);

  return () => {
    const current = sharedDatabaseSubscriptions.get(key);
    if (!current) return;

    current.listeners.delete(callback);
    if (current.listeners.size > 0) return;

    current.unsubscribe?.();
    sharedDatabaseSubscriptions.delete(key);
  };
}

/**
 * Fetches a single database by key.
 *
 * @param {string} key - The database key to fetch.
 *
 * @returns {Promise<Object|null>} The database object or null if not found.
 */
export async function getDatabase(key) {

  // Validate key
  if (!key) return null;

  try {
    const ref = doc(db, DATABASES_COLLECTION, key);
    const snap = await getDoc(ref);

    if (!snap.exists()) return null;

    const data = snap.data() || {};
    if (data?.key && data.key !== snap.id) {
      console.warn(`[DatabaseReadService] getDatabase key mismatch for doc ${snap.id}: stored key=${data.key}`);
    }

    return { id: snap.id, ...data, key: snap.id };
  } catch (error) {
    console.error('[DatabaseReadService] getDatabase error:', error);
    throw error;
  }
}

/**
 * Fetches a single database by key within a workspace using a list-safe query.
 *
 * Avoids direct document reads for deterministic ids that may not exist yet,
 * which can surface as `permission-denied` under the current Firestore rules.
 *
 * @param {string} workspaceKey - The workspace key to scope databases.
 * @param {string} key - The database key to fetch.
 *
 * @returns {Promise<Object|null>} The database object or null if not found.
 */
export async function getWorkspaceDatabaseByKey(workspaceKey, key) {

  // Validate inputs
  if (!workspaceKey || !key) return null;

  try {
    const q = query(
      collection(db, DATABASES_COLLECTION),
      where('workspaceKey', '==', workspaceKey)
    );
    const snapshot = await getDocs(q);
    const match = snapshot.docs.find((docSnap) => docSnap.id === key);

    if (!match?.exists()) return null;

    return {
      id: match.id,
      ...(match.data() || {}),
      key: match.id,
    };
  } catch (error) {
    console.error('[DatabaseReadService] getWorkspaceDatabaseByKey error:', error);
    throw error;
  }
}
